import React, { useEffect, useState, useCallback } from 'react';
import {
    Row,
    Col,
    Form,
    Button,
    Table,
    Spinner,
    Alert,
    Badge,
} from 'react-bootstrap';
import { fetchBusinessDays } from '../../../firebase-config';

/**
 * 営業日管理ページ.
 */
function BusinessDaysPage({ myEmployee }) {
    const [monthsParam, setMonthsParam] = useState(3);   // 何ヶ月分取得するか
    const [lowTime, setLowTime] = useState(140);         // 下限時間

    const [basicTime, setBasicTime] = useState(8);
    const [months, setMonths] = useState([]);            // API から返る months[]

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "営業時間確認";
    }, []);

    const loadBusinessDays = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchBusinessDays(monthsParam, lowTime);
            setBasicTime(data.basicTime);
            setMonths(data.months || []);
        } catch (e) {
            console.error(e);
            setError('営業日の取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    }, [monthsParam, lowTime]);

    useEffect(() => {
        loadBusinessDays();
    }, [loadBusinessDays]);

    const formatYearMonth = (year, month) =>
        `${year}年${month.toString().padStart(2, '0')}月`;

    return (
        <Row className="mt-4">
            {/* タイトル + 再読み込みボタン */}
            <Row className="align-items-center">
                <Col>
                    <h1>営業時間確認</h1>
                </Col>
                <Col md="auto" className="text-end">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={loadBusinessDays}
                        disabled={loading}
                    >
                        再読み込み
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

            {/* パラメータ設定フォーム */}
            <Form className="mt-4 mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>表示月数</Form.Label>
                            <Form.Select
                                value={monthsParam}
                                onChange={(e) => setMonthsParam(Number(e.target.value))}
                            >
                                <option value={3}>3ヶ月</option>
                                <option value={6}>6ヶ月</option>
                                <option value={10}>10ヶ月</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>下限時間</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                value={lowTime}
                                onChange={(e) => setLowTime(Number(e.target.value))}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                        <div className="text-muted">
                            基本稼働時間: <strong>{basicTime}時間/日</strong>
                        </div>
                    </Col>
                </Row>
            </Form>

            {/* 一覧 */}
            <Row className="mt-3">
                {loading && (
                    <div className="mt-3 mb-3">
                        <Spinner animation="border" variant="dark" />
                    </div>
                )}

                {!loading && months.length === 0 && (
                    <p className="mt-3">営業日情報がありません。</p>
                )}

                {!loading && months.length > 0 && (
                    <Table striped bordered hover className="mt-3">
                        <thead>
                            <tr>
                                <th>年月</th>
                                <th>稼働日</th>
                                <th>基本稼働時間</th>
                                <th>土日</th>
                                <th>休業日</th>
                                <th>余裕日数</th>
                                <th>ステータス</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map((m) => (
                                <tr key={`${m.year}-${m.month}`}>
                                    <td>{formatYearMonth(m.year, m.month)}</td>
                                    <td>{m.workingDays}日</td>
                                    <td>{m.workingHours}時間</td>
                                    <td>{m.weekendDays}日</td>
                                    <td>{m.closedDays}日</td>
                                    <td>{m.spareDays}日</td>
                                    <td>
                                        {m.spareDays < 0 ? (
                                            <Badge bg="danger">危険</Badge>
                                        ) : m.workingDays < 20 ? (
                                            <Badge bg="warning">警告</Badge>
                                        ) : (
                                            <Badge bg="primary">OK</Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Row>

            <Alert variant="info" className="mt-3">
                <strong>ステータスについて</strong>
                <ul className="mb-0">
                    <li><Badge bg="primary">OK</Badge>：問題なし</li>
                    <li><Badge bg="warning">警告</Badge>：稼働日数が少なめ</li>
                    <li><Badge bg="danger">危険</Badge> ：余裕日数がマイナス</li>
                </ul>
            </Alert>
        </Row>
    );
}

export default BusinessDaysPage;
